import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  LegacyCard,
  ContextualSaveBar,
  FormLayout,
  Frame,
  Icon,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  TextContainer,
  Text,
  TextField,
  Toast,
  Banner,
  Tooltip,
  Checkbox,
  Scrollable,
  EmptyState,
  UnstyledLink,
  LegacyStack,
  Select,
  DropZone,
  Thumbnail,
  Autocomplete,
  HorizontalStack,
  Tag,
} from '@shopify/polaris';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import axios from 'axios';

import './VariantNew.scss';
import { TopBarMarkup, NavigationMarkup, contextControlMarkup } from '../../../../../components';

import { CancelSmallMinor, DeleteMinor, FraudProtectMajor, NoteMinor, PlusMinor, QuestionMarkMajor, SearchMinor } from '@shopify/polaris-icons';
import { SortableItemQuotation, SortableItemPricingRule, SortableItemWarranty } from '../../Components';

import {
  PricingRule,
  QuotationField,
  Variant,
  Company,
  Warranty,
  Renewal,
  Automation,
  TBlockingCondition,
  TCondition,
  RuleTypes,
  blockingConditionsRuleOptions,
} from '../../../../../types';
import { useUser } from '../../../../../utils/PrivateRoute';
import { useNavigate, useParams } from 'react-router-dom';

type AutocompleteCompanyOption = {
  label: string;
  value: string;
  company: Company;
};

export function VariantNew() {
  const navigate = useNavigate();
  const { user } = useUser();
  const params = useParams();

  const skipToContentRef = useRef<HTMLAnchorElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonSpinning, setButtonSpinning] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [isDirty, setIsDirty] = useState(true);
  const [active, setActive] = useState(false);

  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((mobileNavigationActive) => !mobileNavigationActive), []);

  const handleMobileNavigation = (data: boolean) => {
    setMobileNavigationActive(!data);
  };

  /**
   * Drag & Drop quotation fields
   */
  const [quotationOptions, setQuotationOptions] = useState(['1']);
  const sensorsQuotation = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEndQuotation(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setQuotationOptions((options) => {
        const oldIndex = options.indexOf(active.id);
        const newIndex = options.indexOf(over.id);

        return arrayMove(options, oldIndex, newIndex);
      });
    }
  }

  /**
   * Drag & Drop pricing rules
   */
  const [pricingRulesOptions, setPricingRulesOptions] = useState(['1']);
  const sensorsPricingRules = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEndPricingRules(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPricingRulesOptions((options) => {
        const oldIndex = options.indexOf(active.id);
        const newIndex = options.indexOf(over.id);

        return arrayMove(options, oldIndex, newIndex);
      });
    }
  }

  /**
   * Drag & Drop
   */
  const [warranties, setWarranties] = useState(['1']);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setWarranties((warranties) => {
        const oldIndex = warranties.indexOf(active.id);
        const newIndex = warranties.indexOf(over.id);

        return arrayMove(warranties, oldIndex, newIndex);
      });
    }
  }

  /**
   * Variant states
   */
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Variant type
  const [selectedVariantType, setSelectedVariantType] = useState('basic');
  const variantTypeOptions = [
    { label: 'Base', value: 'basic' },
    { label: 'API', value: 'api' },
  ];

  // Api identifier
  const [apiIdentifier, setApiIdentifier] = useState('');

  // Group policy
  const [groupPolicy, setGroupPolicy] = useState(false);
  const [groupPolicyBroker, setGroupPolicyBroker] = useState('');
  const [groupPolicyBranch, setGroupPolicyBranch] = useState('');
  const [groupPolicyNumber, setGroupPolicyNumber] = useState('');
  const [groupPolicyInclusionNumber, setGroupPolicyInclusionNumber] = useState('');

  //To be Quoted
  const [toBeQuoted, setToBeQuoted] = useState(false);

  // Direct emission
  const [directEmission, setDirectEmission] = useState(false);

  //Bank Transfer
  const [bankTransfer, setBankTransfer] = useState(false);

  // Disable notifications
  const [disableNotifications, setDisableNotifications] = useState(false);

  // Blocking conditions
  const [hasBlockingConditions, setHasBlockingConditions] = useState(false);
  const [blockingConditions, setBlockingConditions] = useState<TBlockingCondition[]>([]);

  // Company
  const [deselectedCompaniesOptions, setDeselectedCompaniesOptions] = useState<AutocompleteCompanyOption[]>([]);
  const [selectedCompanyOptions, setSelectedCompanyOptions] = useState([]);
  const [companyInputValue, setCompanyInputValue] = useState('');
  const [companyOptions, setCompanyOptions] = useState(deselectedCompaniesOptions);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);

  // Quotation
  const [quotation, setQuotation] = useState(false);

  const [price, setPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');

  const [hasOptions, setHasOptions] = useState(false);

  // Quotation fields
  const quotationFieldsType = [
    { label: 'Data', value: 'date' },
    { label: 'Array', value: 'array' },
    { label: 'Numero', value: 'number' },
    { label: 'Percentuale', value: 'percentage' },
    { label: 'Valuta', value: 'currency' },
    { label: 'Testo', value: 'text' },
    { label: 'Checkbox', value: 'boolean' },
    { label: 'Select', value: 'select' },
  ];

  const [quotationFields, setQuotationFields] = useState<QuotationField[]>([]);

  // Commission
  const [hasCommission, setHasCommission] = useState(false);
  const [commission, setCommission] = useState({
    value: '0,00',
    value_type: 'fixed_amount',
  });

  const commissionValueTypeOptions = useMemo(
    () => [
      { label: 'Importo', value: 'fixed_amount' },
      { label: 'Percentuale', value: 'percentage' },
    ],
    [],
  );

  // Management price
  const [hasManagementPrice, setHasManagementPrice] = useState(false);
  const [managementPrice, setManagementPrice] = useState({
    value: '0,00',
    value_type: 'fixed_amount',
  });

  // Shared Notification
  const [hasSharedNotification, setHasSharedNotification] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [tagError, setTagError] = useState(false);

  // Automation Bot
  const [hasAutomationBot, setHasAutomationBot] = useState(false);
  const [automationName, setAutomationName] = useState('better-bookings');
  const [automationOptions, setAutomationOptions] = useState([]);

  // Renewal
  const [renewal, setRenewal] = useState<Renewal>({
    automatic: false,
    to_be_confirmed: false,
  });

  // Pricing rules
  const [hasPricingRules, setHasPricingRules] = useState(false);
  const pricingRulesNames = [{ label: 'Prezzo minimo', value: 'minimum_price' }];
  const pricingRulesTypes = [
    { label: 'Importo', value: 'fixed_amount' },
    { label: 'Percentuale', value: 'percentage' },
  ];

  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);

  // Variants
  const [hasWarranties, setHasWarranties] = useState(false);
  const [variants, setVariants] = useState<Variant[]>([]);

  const variantWarrantiesModifierType = [
    { label: 'Import Fisso', value: 'fixed_price' },
    { label: 'Percentuale', value: 'percentage' },
    { label: 'Nessuno', value: 'none' },
  ];

  const [variantWarranties, setVariantWarranties] = useState<Warranty[]>([]);

  // Files
  const [files, setFiles] = useState<File[]>([]);
  const [openFileDialog, setOpenFileDialog] = useState(false);

  // Empty fields
  const [emptyFields, setEmptyFields] = useState({
    name: false,
    price: false,
    company: false,
    category: false,
    commission_value: false,
    management_price_value: false,
    api_identifier: false,
    hasWarranties: false,
    variantWarranties: [],
  });

  // Fetch available automation options
  useEffect(() => {
    const fetchAllowedAutomations = async () => {
      try {
        const response = await axios.get((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '/api') + '/admin/automations/allowed', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        });
        const data = response.data;

        if (data.status === 'success') {
          const options = data.data.map((option: Automation) => ({
            label: option.name,
            value: option.name,
          }));

          setAutomationOptions(options);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchAllowedAutomations();
  }, []);

  /**
   * Fetch product data
   */
  useEffect(() => {
    async function fetchProductVariants() {
      try {
        setIsLoading(true);
        const response = await fetch((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '/api') + `/admin/products/${params.id}/variants`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        });
        const data = await response.json();

        if (data.status === 'success') {
          const variants = data.data;
          setVariants(variants);
          setLoadError(false);
        } else {
          setLoadError(true);
        }
      } catch (err) {
        console.log(err);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchCompanies() {
      try {
        setCompanyLoading(true);
        const response = await axios.get((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '/api') + `/admin/companies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('mb__access_token')}` },
          timeout: 10000,
        });
        const data = response.data;

        if (data.status === 'success') {
          const companies = data.data.map((company: Company) => {
            return {
              label: company.name,
              value: company._id,
              company: company,
            };
          });
          setCompanyOptions(companies);
          setDeselectedCompaniesOptions(companies);
        }
      } catch (err: any) {
        console.log(err);
      } finally {
        setCompanyLoading(false);
      }
    }
    fetchProductVariants().then(() => {
      fetchCompanies();
    });
  }, [params.id, user.token]);

  /**
   * Save data
   */
  const handleSave = useCallback(async () => {
    try {
      setButtonSpinning(true);

      // Check if fields are empty
      let errFlag = false;
      if (name === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, name: true }));
        errFlag = true;
      }

      if (price === '' && !quotation && quotationFields.length === 0) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, price: true }));
        errFlag = true;
      }

      if (hasCommission && commission.value === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, commission_value: true }));
        errFlag = true;
      }

      if (hasManagementPrice && managementPrice.value === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, management_price_value: true }));
        errFlag = true;
      }

      if (selectedVariantType === 'api' && apiIdentifier === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, api_identifier: true }));
        errFlag = true;
      }

      if (company === null) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, company: true }));
        errFlag = true;
      }

      if (errFlag) {
        setButtonSpinning(false);
        return;
      }

      // Create form data
      const formData = new FormData();

      // Files
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          formData.append('documents', files[i]);
        }
      }

      // Pricing rules
      if (hasPricingRules) {
        pricingRules.forEach((rule: PricingRule, index: number) => {
          formData.append(`pricing_rules[${index}][name]`, rule.name);
          formData.append(`pricing_rules[${index}][type]`, rule.type);
          formData.append(`pricing_rules[${index}][value]`, rule.value);
          formData.append(`pricing_rules[${index}][position]`, String(rule.position));
        });
      }

      // Quotation fields
      if (quotationFields.length > 0) {
        quotationFields.forEach((field: QuotationField, index: number) => {
          formData.append(`quotation_fields[${index}][name]`, field.name);
          formData.append(`quotation_fields[${index}][type]`, field.type);
          formData.append(`quotation_fields[${index}][position]`, String(field.position));
          formData.append(`quotation_fields[${index}][result_modifier_type]`, field.result_modifier_type);

          if (field.result_modifier_value) formData.append(`quotation_fields[${index}][result_modifier_value]`, field.result_modifier_value);
          if (field.api_field_name) formData.append(`quotation_fields[${index}][api_field_name]`, field.api_field_name);
          if (field.values && field.values?.length > 0) {
            field.values.forEach((value: { label: string; value: string } | QuotationField, index2: number) => {
              // Used when type select is selected
              if (isLabelValuePair(value)) {
                formData.append(`quotation_fields[${index}][values][${index2}][label]`, value.label);
                formData.append(`quotation_fields[${index}][values][${index2}][value]`, value.value);
              } else {
                formData.append(`quotation_fields[${index}][values][${index2}][name]`, value.name);
                formData.append(`quotation_fields[${index}][values][${index2}][type]`, value.type);
                if (value.api_field_name) {
                  formData.append(`quotation_fields[${index}][values][${index2}][api_field_name]`, value.api_field_name);
                }
              }
            });
          }
        });
      }

      // Variant warranties
      if (variantWarranties.length > 0) {
        variantWarranties.forEach((warranty: Warranty, index: number) => {
          formData.append(`warranties[${index}][name]`, warranty.name);
          formData.append(`warranties[${index}][included]`, warranty.included ? 'true' : 'false');
          formData.append(`warranties[${index}][result_modifier_type]`, warranty.result_modifier_type);
          if (warranty.result_modifier_value) formData.append(`warranties[${index}][result_modifier_value]`, warranty.result_modifier_value);
          formData.append(`warranties[${index}][value]`, warranty.value);
          formData.append(`warranties[${index}][position]`, String(warranty.position));
        });
      }

      formData.append('name', name);
      formData.append('description', description);
      formData.append('price', price);
      formData.append('type', selectedVariantType);
      if (company?._id) {
        formData.append('company', company._id);
      }

      // Api identifier
      if (selectedVariantType === 'api') {
        formData.append('api_id', apiIdentifier);
      }

      formData.append('sale_price', salePrice);
      formData.append('group_policy', String(groupPolicy));
      if (groupPolicy) {
        formData.append('group_policy_broker', groupPolicyBroker);
        formData.append('group_policy_branch', groupPolicyBranch);
        formData.append('group_policy_number', groupPolicyNumber);
        formData.append('group_policy_inclusion_number_prefix', groupPolicyInclusionNumber);
      }
      formData.append('to_be_quoted', String(toBeQuoted));
      formData.append('direct_emission', String(directEmission));
      formData.append('bank_transfer', String(bankTransfer));
      formData.append('disable_notifications', String(disableNotifications));
      formData.append('quotation', String(quotation));
      if (hasCommission) {
        formData.append('commission[value]', commission.value);
        formData.append('commission[value_type]', commission.value_type);
      }
      if (hasManagementPrice) {
        formData.append('management_price[value]', managementPrice.value);
        formData.append('management_price[value_type]', managementPrice.value_type);
      }

      // Shared notification
      if (hasSharedNotification) {
        selectedTags.forEach((tag: string, index: number) => {
          formData.append(`shared_notification[email_list][${index}]`, tag);
        });
      }

      // Blocking conditions
      formData.append('has_blocking_conditions', String(hasBlockingConditions));
      if (hasBlockingConditions) {
        blockingConditions.forEach((condition: TBlockingCondition, index: number) => {
          formData.append(`blocking_conditions[${index}][field]`, condition.field);
          condition.conditions.forEach((cond: TCondition, index2: number) => {
            formData.append(`blocking_conditions[${index}][conditions][${index2}][rule]`, cond.rule);
            formData.append(`blocking_conditions[${index}][conditions][${index2}][value]`, cond.value);
          });
        });
      }

      // Automation bot
      formData.append('has_automation_bot', String(hasAutomationBot));
      if (hasAutomationBot) {
        formData.append('automation_name', automationName);
      }

      // Renewal
      formData.append('renewal[automatic]', String(renewal.automatic));
      formData.append('renewal[to_be_confirmed]', String(renewal.to_be_confirmed));

      const response = await axios.post(
        (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '/api') + `/admin/products/${params.id}/variants/new`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        },
      );
      const data = response.data;

      if (data.status === 'success') {
        setActive(true);
        setTimeout(() => {
          navigate(`/admin/products/${params.id}/variants/${data.data._id}`);
        }, 3000);
        setIsDirty(false);
      } else {
        setSaveError(true);
      }
    } catch (error) {
      console.log(error);
      setSaveError(true);
    } finally {
      setButtonSpinning(false);
    }
  }, [
    name,
    description,
    company,
    selectedVariantType,
    apiIdentifier,
    price,
    salePrice,
    groupPolicy,
    groupPolicyBroker,
    groupPolicyBranch,
    groupPolicyNumber,
    groupPolicyInclusionNumber,
    hasSharedNotification,
    hasBlockingConditions,
    blockingConditions,
    hasAutomationBot,
    automationName,
    selectedTags,
    toBeQuoted,
    directEmission,
    bankTransfer,
    disableNotifications,
    variantWarranties,
    history,
    quotation,
    quotationFields,
    hasPricingRules,
    pricingRules,
    hasCommission,
    commission,
    hasManagementPrice,
    managementPrice,
    files,
    params.id,
    renewal,
  ]);

  /**
   * Policy Handlers
   */
  const handleNameChange = useCallback(
    (e: string) => {
      // If emptyFields.name is true, set it to false
      if (emptyFields.name) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, name: false }));
      }
      setName(e);
      setIsDirty(true);
    },
    [emptyFields.name],
  );

  // Description
  const handleDescriptionChange = useCallback((e: string) => {
    setDescription(e);
    setIsDirty(true);
  }, []);

  // Variant type
  const handleVariantTypeChange = useCallback((e: any) => {
    setSelectedVariantType(e);
    setIsDirty(true);
  }, []);

  // Api identifier handler
  const handleApiIdentifierChange = useCallback(
    (e: string) => {
      // If emptyFields.api_identifier is true, set it to false
      if (emptyFields.api_identifier) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, api_identifier: false }));
      }

      setApiIdentifier(e);
      setIsDirty(true);
    },
    [apiIdentifier, emptyFields.api_identifier],
  );

  // Group policy
  const handleGroupPolicyChange = useCallback(() => {
    setGroupPolicy(!groupPolicy);
    setIsDirty(true);
  }, [groupPolicy]);

  // Group policy broker
  const handleGroupPolicyBrokerChange = useCallback((e: string) => {
    setGroupPolicyBroker(e);
    setIsDirty(true);
  }, []);

  // Group policy branch
  const handleGroupPolicyBranchChange = useCallback((e: string) => {
    setGroupPolicyBranch(e);
    setIsDirty(true);
  }, []);

  // Group policy number
  const handleGroupPolicyNumberChange = useCallback((e: string) => {
    setGroupPolicyNumber(e);
    setIsDirty(true);
  }, []);

  // Group policy inclusion number
  const handleGroupPolicyInclusionNumberChange = useCallback((e: string) => {
    setGroupPolicyInclusionNumber(e);
    setIsDirty(true);
  }, []);

  //To be Quoted
  const handleToBeQuotedChange = useCallback(() => {
    setToBeQuoted(!toBeQuoted);
    setIsDirty(true);
  }, [toBeQuoted]);

  // Direct emission
  const handleDirectEmissionChange = useCallback(() => {
    setDirectEmission(!directEmission);
    setIsDirty(true);
  }, [directEmission]);

  // Bank transfer
  const handleBankTransferChange = useCallback(() => {
    setBankTransfer(!bankTransfer);
    setIsDirty(true);
  }, [bankTransfer]);

  // Disable notifications
  const handleDisableNotificationsChange = useCallback(() => {
    setDisableNotifications(!disableNotifications);
    setIsDirty(true);
  }, [disableNotifications]);

  // Quotation
  const handleQuotationChange = useCallback(
    (e: any) => {
      setQuotation(e);
      setIsDirty(true);
    },
    [quotation],
  );

  // Blocking conditions
  const handleHasBlockingConditionsChange = useCallback(() => {
    // If set to true, populate the blocking conditions array with a default object
    if (!hasBlockingConditions && blockingConditions.length === 0) {
      setBlockingConditions([
        {
          field: '',
          conditions: [{ rule: RuleTypes.EQUAL, value: '' }],
        },
      ]);
    }
    setHasBlockingConditions(!hasBlockingConditions);
    setIsDirty(true);
  }, [hasBlockingConditions]);

  const handleBlockingConditionFieldChange = useCallback(
    (index: number, field: string) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].field = field;
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleBlockingConditionValueChange = useCallback(
    (index: number, conditionIndex: number, value: string) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions[conditionIndex].value = value;
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleBlockingConditionRuleChange = useCallback(
    (index: number, conditionIndex: number, rule: RuleTypes) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions[conditionIndex].rule = rule;
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleAddBlockingCondition = useCallback(() => {
    setBlockingConditions((prevConditions) => [
      ...prevConditions,
      {
        field: '',
        conditions: [{ rule: RuleTypes.EQUAL, value: '' }],
      },
    ]);
    setIsDirty(true);
  }, []);

  const handleAddBlockingConditionRule = useCallback(
    (index: number) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions.push({ rule: RuleTypes.EQUAL, value: '' });
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleDeleteBlockingCondition = useCallback(
    (index: number) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions.splice(index, 1);
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleDeleteBlockingConditionRule = useCallback(
    (index: number, subIndex: number) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions.splice(subIndex, 1);
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handlePriceChange = useCallback(
    (e: any) => {
      // If emptyFields.price is true, set it to false
      if (emptyFields.price) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, price: false }));
      }

      // Check if the price has a dot and replace it with a comma
      if (e.includes('.')) {
        e = e.replace('.', ',');
      }

      // Check if the price has 2 or more commas and remove the last one
      if (e.split(',').length > 2) {
        e = e.replace(/,(?=[^,]*$)/, '');
      }

      // Check if the price has 3 or more numbers after the comma and remove the last one
      if (e.split(',')[1] && e.split(',')[1].length > 2) {
        e = e.replace(/.$/, '');
      }

      // Check if there are letters or special characters and remove them
      if (e.match(/[^0-9,]/g)) {
        e = e.replace(/[^0-9,]/g, '');
      }

      setPrice(e);
      setIsDirty(true);
    },
    [emptyFields.price],
  );

  const handleSalePriceChange = useCallback((e: any) => {
    // Check if the price has a dot and replace it with a comma
    if (e.includes('.')) {
      e = e.replace('.', ',');
    }

    // Check if the price has 2 or more commas and remove the last one
    if (e.split(',').length > 2) {
      e = e.replace(/,(?=[^,]*$)/, '');
    }

    // Check if the price has 3 or more numbers after the comma and remove the last one
    if (e.split(',')[1] && e.split(',')[1].length > 2) {
      e = e.replace(/.$/, '');
    }

    // Check if there are letters or special characters and remove them
    if (e.match(/[^0-9,]/g)) {
      e = e.replace(/[^0-9,]/g, '');
    }

    setSalePrice(e);
    setIsDirty(true);
  }, []);

  /** Pricing rules */
  const handleHasPricingRulesChange = useCallback(() => {
    setHasPricingRules(!hasPricingRules);
    setIsDirty(true);
  }, [hasPricingRules]);

  /** Commission */
  const handleHasCommissionChange = useCallback(() => {
    setHasCommission(!hasCommission);
    setIsDirty(true);
  }, [hasCommission]);

  const handleCommissionValueChange = useCallback(
    (e: any) => {
      // If emptyFields.commission_value is true, set it to false
      if (emptyFields.commission_value) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, commission_value: false }));
      }

      // Check if the price has 2 or more commas and remove the last one
      if (e.split(',').length > 2) {
        e = e.replace(/,(?=[^,]*$)/, '');
      }

      // Check if there are letters or special characters and remove them
      if (e.match(/[^0-9,]/g)) {
        e = e.replace(/[^0-9,]/g, '');
      }

      // If commission type is percentage, check if the value is greater than 100
      if (commission.value_type === 'percentage' && parseFloat(e.replace(',', '.')) > 100) {
        e = '100';
      }

      setCommission({ ...commission, value: e });
      setIsDirty(true);
    },
    [commission, emptyFields.commission_value],
  );

  const handleCommissionTypeChange = useCallback(
    (e: any) => {
      setCommission({ ...commission, value_type: e });
      setIsDirty(true);
    },
    [commission],
  );

  /** Management Price */
  const handleHasManagementPriceChange = useCallback(() => {
    setHasManagementPrice(!hasManagementPrice);
    setIsDirty(true);
  }, [hasManagementPrice]);

  const handleManagementPriceValueChange = useCallback(
    (e: any) => {
      // If emptyFields.management_price_value is true, set it to false
      if (emptyFields.management_price_value) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, management_price_value: false }));
      }

      // Check if the price has 2 or more commas and remove the last one
      if (e.split(',').length > 2) {
        e = e.replace(/,(?=[^,]*$)/, '');
      }

      // Check if there are letters or special characters and remove them
      if (e.match(/[^0-9,]/g)) {
        e = e.replace(/[^0-9,]/g, '');
      }

      // If commission type is percentage, check if the value is greater than 100
      if (managementPrice.value_type === 'percentage' && parseFloat(e.replace(',', '.')) > 100) {
        e = '100';
      }

      setManagementPrice({ ...managementPrice, value: e });
      setIsDirty(true);
    },
    [managementPrice, emptyFields.management_price_value],
  );

  const handleManagementPriceTypeChange = useCallback(
    (e: any) => {
      setManagementPrice({ ...managementPrice, value_type: e });
      setIsDirty(true);
    },
    [managementPrice],
  );

  /** Shared notification */
  const handleHasSharedNotificationChange = useCallback(() => {
    setHasSharedNotification(!hasSharedNotification);
    setIsDirty(true);
  }, [hasSharedNotification]);

  const handleHasWarrantiesChange = useCallback(() => {
    setHasWarranties(!hasWarranties);
    setIsDirty(true);
  }, [hasWarranties]);

  /** Automation Bot */
  const handleHasAutomationBotChange = useCallback(() => {
    setHasAutomationBot(!hasAutomationBot);
    setIsDirty(true);
  }, [hasAutomationBot]);

  const handleAutomationSelectChange = useCallback((e: string) => {
    console.log(e);
    setAutomationName(e);
    setIsDirty(true);
  }, []);

  /** Validate email */
  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  /**
   * Add warranty option
   */
  const handleAddWarrantyOption = useCallback(() => {
    setWarranties((warranties) => [...warranties, String(warranties.length + 1)]);
  }, []);

  /**
   * Remove warranty option
   */
  const handleRemoveWarranty = useCallback(
    (id: string) => {
      setWarranties((warranties) => warranties.filter((warranty) => warranty !== id));
      // Remove the warranty from the variantWarranties array if it exists
      setVariantWarranties((variantWarranties) => {
        const newVariantOptions = variantWarranties.filter((warranty) => String(warranty.position) !== id);
        return newVariantOptions;
      });
    },
    [setWarranties],
  );

  /**
   * Add variant option
   */
  const handleAddVariantWarranty = useCallback(
    (
      position: string,
      name: string,
      value: string,
      included: boolean,
      deductible: string,
      result_modifier_type: 'fixed_amount' | 'percentage' | 'none',
      result_modifier_value?: string,
    ) => {
      setVariantWarranties((variantWarranties) => [
        ...variantWarranties,
        {
          name: name,
          value: value,
          position: Number(position),
          result_modifier_type: result_modifier_type,
          included: included,
          deductible: deductible,
          result_modifier_value: result_modifier_value,
        },
      ]);
      setIsDirty(true);
    },
    [],
  );

  /**
   * Update variant option
   */
  const handleUpdateVariantWarranty = useCallback(
    (
      id: string,
      position: string,
      name: string,
      value: string,
      included: boolean,
      deductible: string,
      result_modifier_type: 'fixed_amount' | 'percentage' | 'none',
      result_modifier_value?: string,
    ) => {
      const tmp = variantWarranties;
      const index = tmp.findIndex((warranty: any) => String(warranty.position) === id);
      tmp[index] = {
        name: name,
        value: value,
        position: Number(position),
        result_modifier_type: result_modifier_type,
        included: included,
        deductible: deductible,
        result_modifier_value: result_modifier_value,
      };
      setVariantWarranties(tmp);
      setIsDirty(true);
    },
    [variantWarranties],
  );

  /**
   * Warranty markup
   */
  const warrantyMarkup = (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={quotationOptions} strategy={verticalListSortingStrategy}>
          {warranties.map((id, index) => (
            <SortableItemWarranty
              key={id}
              id={id}
              warrantyItem={variantWarranties[index]}
              options={variantWarrantiesModifierType}
              onDelete={handleRemoveWarranty}
              onSave={handleAddVariantWarranty}
              onUpdate={handleUpdateVariantWarranty}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddWarrantyOption}>
          Aggiungi garanzia
        </Button>
      </LegacyCard.Section>
    </>
  );

  /**
   * Add quotation option
   */
  const handleAddQuotationOption = useCallback(() => {
    setQuotationOptions((options) => [...options, String(options.length + 1)]);
  }, []);

  /**
   * Remove quotation field
   */
  const handleRemoveQuotationField = useCallback(
    (id: string) => {
      setQuotationOptions((options) => options.filter((option) => option !== id));
      // Remove the option from the quotationFields array if it exists
      const tmp = quotationFields;
      const index = tmp.findIndex((option) => String(option.position) === id);
      tmp.splice(index, 1);
      setQuotationFields(tmp);
      setIsDirty(true);
    },
    [setQuotationOptions, quotationFields],
  );

  /**
   * Add quotation field
   */
  const handleAddQuotationField = useCallback(
    (
      position: string,
      name: string,
      type: 'select' | 'percentage' | 'number' | 'boolean' | 'text' | 'currency' | 'array' | 'date',
      result_modifier_type: 'fixed_amount' | 'percentage' | 'pricing_table' | 'none',
      // values?: Array<{ label: string; value: string }>,
      values?: string,
      result_modifier_value?: string,
      api_field_name?: string,
    ) => {
      // If values is not empty, convert it to a JSON array and check if each object has a label and a value
      let tmp: Array<{ label: string; value: string }> = [];
      if (values) {
        try {
          if (typeof values === 'string') {
            tmp = JSON.parse(values);
            tmp.forEach((value) => {
              if (!isLabelValuePair(value)) {
                throw new Error('Invalid values');
              }
            });
          } else if (Array.isArray(values)) {
            tmp = values;
          }
        } catch (err) {
          console.log(err);
          return;
        }
      }
      console.log(tmp);

      setQuotationFields((quotationFields) => [
        ...quotationFields,
        {
          name: name,
          type: type,
          values: tmp,
          position: Number(position),
          result_modifier_type: result_modifier_type,
          result_modifier_value: result_modifier_value,
          api_field_name: api_field_name,
        },
      ]);
      setIsDirty(true);
    },
    [],
  );

  /**
   * Update quotation field
   */
  const handleUpdateQuotationField = useCallback(
    (
      _id: string,
      position: string,
      name: string,
      type: 'select' | 'percentage' | 'number' | 'boolean' | 'text' | 'currency' | 'array' | 'date',
      result_modifier_type: 'fixed_amount' | 'percentage' | 'pricing_table' | 'none',
      // values?: Array<{ label: string; value: string }>,
      values?: string,
      result_modifier_value?: string,
      api_field_name?: string,
    ) => {
      const tmp = quotationFields;
      const index = tmp.findIndex((field: any) => field._id === _id);
      // If values is not empty, convert it to a JSON array and check if each object has a label and a value
      let tmpValues: Array<{ label: string; value: string }> = [];
      if (values) {
        try {
          if (typeof values === 'string') {
            tmpValues = JSON.parse(values);
            tmpValues.forEach((value) => {
              if (!isLabelValuePair(value)) {
                throw new Error('Invalid values');
              }
            });
          } else if (Array.isArray(values)) {
            tmpValues = values;
          }
        } catch (err) {
          console.log(err);
          return;
        }
      }
      tmp[index] = {
        name: name,
        position: Number(position),
        type: type,
        result_modifier_type: result_modifier_type,
        result_modifier_value: result_modifier_value,
        values: tmpValues,
        api_field_name: api_field_name,
      };

      setQuotationFields(tmp);
      setIsDirty(true);
    },
    [quotationFields],
  );

  /**
   * Quotation markup
   */
  const quotationMarkup = (
    <>
      <DndContext sensors={sensorsQuotation} collisionDetection={closestCenter} onDragEnd={handleDragEndQuotation}>
        <SortableContext items={quotationOptions} strategy={verticalListSortingStrategy}>
          {quotationOptions.map((id, index) => (
            <SortableItemQuotation
              key={id}
              id={id}
              quotationItem={quotationFields[index]}
              options={quotationFieldsType}
              onDelete={handleRemoveQuotationField}
              onSave={handleAddQuotationField}
              onUpdate={handleUpdateQuotationField}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddQuotationOption}>
          Aggiungi campo quotazione
        </Button>
      </LegacyCard.Section>
    </>
  );

  /**
   * Add pricing rule option
   */
  const handleAddPricingRulesOption = useCallback(() => {
    setPricingRulesOptions((options) => [...options, String(options.length + 1)]);
  }, []);

  /**
   * Remove quotation field
   */
  const handleRemovePricingRule = useCallback(
    (id: string) => {
      setPricingRulesOptions((options) => options.filter((option) => option !== id));
      // Remove the option from the pricingRules array if it exists
      const tmp = pricingRules;
      const index = tmp.findIndex((option) => String(option.position) === id);
      tmp.splice(index, 1);
      setPricingRules(tmp);
      setIsDirty(true);
    },
    [setPricingRulesOptions, pricingRules],
  );

  /**
   * Add quotation field
   */
  const handleAddPricingRule = useCallback((position: string, name: 'minimum_price', type: 'fixed_amount' | 'percentage', value: string) => {
    setPricingRules((pricingRules) => [...pricingRules, { name: name, type: type, value: value, position: Number(position) }]);
    setIsDirty(true);
  }, []);

  /**
   * Update quotation field
   */
  const handleUpdatePricingRule = useCallback(
    (_id: string, position: string, name: 'minimum_price', type: 'fixed_amount' | 'percentage', value: string) => {
      const tmp = pricingRules;
      const index = tmp.findIndex((field: any) => field._id === _id);
      tmp[index] = { name: name, type: type, value: value, position: Number(position) };

      setPricingRules(tmp);
      setIsDirty(true);
    },
    [pricingRules],
  );

  /**
   * Pricing rules markup
   */
  const pricingRulesMarkup = (
    <>
      <DndContext sensors={sensorsPricingRules} collisionDetection={closestCenter} onDragEnd={handleDragEndPricingRules}>
        <SortableContext items={pricingRulesOptions} strategy={verticalListSortingStrategy}>
          {pricingRulesOptions.map((id, index) => (
            <SortableItemPricingRule
              key={id}
              id={id}
              pricingRuleItem={pricingRules[index]}
              nameOptions={pricingRulesNames}
              typeOptions={pricingRulesTypes}
              onDelete={handleRemovePricingRule}
              onSave={handleAddPricingRule}
              onUpdate={handleUpdatePricingRule}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddPricingRulesOption}>
          Aggiungi regola
        </Button>
      </LegacyCard.Section>
    </>
  );

  // Define a type guard for { label: string; value: string; }
  function isLabelValuePair(value: any): value is { label: string; value: string } {
    return value && typeof value.label === 'string' && typeof value.value === 'string' && value.label !== '' && value.value !== '';
  }

  /**
   * Drop zone
   */
  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => setFiles((files) => [...files, ...acceptedFiles]),
    [],
  );

  const toggleOpenFileDialog = useCallback(() => setOpenFileDialog((openFileDialog) => !openFileDialog), []);

  const fileUpload = !files.length && <DropZone.FileUpload actionHint="Accetta pdf" />;
  const uploadedFiles = files.length > 0 && (
    <div style={{ padding: '0' }}>
      <LegacyStack vertical>
        {files.map((file, index) => (
          <LegacyStack alignment="center" key={index}>
            <Thumbnail size="small" alt={file.name} source={NoteMinor as any} />
            <div>
              {file.name} <p>{file.size} bytes</p>
            </div>
          </LegacyStack>
        ))}
      </LegacyStack>
    </div>
  );

  const dropZoneMarkup = (
    <DropZone onDrop={handleDropZoneDrop} accept="application/pdf" onFileDialogClose={toggleOpenFileDialog} openFileDialog={openFileDialog}>
      {uploadedFiles}
      {fileUpload}
    </DropZone>
  );

  /**
   * Renewal handler
   */
  const handleRenewalAutomaticChange = useCallback(
    (value: any) => {
      // If renewal.automatic is true, set it to false and also set renewal.to_be_confirmed to false
      if (renewal.automatic) {
        setRenewal({ automatic: false, to_be_confirmed: false });
        setIsDirty(true);
        return;
      }
      setRenewal({ ...renewal, automatic: value });
      setIsDirty(true);
    },
    [renewal],
  );

  const handleRenewalToBeConfirmedChange = useCallback(
    (value: any) => {
      setRenewal({ ...renewal, to_be_confirmed: value });
      setIsDirty(true);
    },
    [renewal],
  );

  /**
   * Company autocomplete
   */
  const updateCompanyText = useCallback(
    (value: any) => {
      setCompanyInputValue(value);

      if (!companyLoading) {
        setCompanyLoading(true);
      }

      setTimeout(() => {
        if (value === '') {
          setCompanyOptions(deselectedCompaniesOptions);
          setCompanyLoading(false);
          return;
        }
        const filterRegex = new RegExp(value, 'i');
        const resultOptions = deselectedCompaniesOptions.filter((option: any) => option.label.match(filterRegex));
        setCompanyOptions(resultOptions);
        setCompanyLoading(false);
      }, 300);
    },
    [deselectedCompaniesOptions, companyLoading],
  );

  const updateCompanySelection = useCallback(
    (selected: any) => {
      // Check if emptyFields is true
      if (emptyFields.company) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, company: false }));
      }

      const selectedCompany = selected.map((selectedItem: any) => {
        const matchedOption = companyOptions.find((option) => {
          return option.value.match(selectedItem);
        });
        return matchedOption;
      });
      setSelectedCompanyOptions(selected);
      setCompanyInputValue(selectedCompany[0].label);

      // Add company
      const company = selectedCompany[0].company;
      setCompany(company);
    },
    [companyOptions],
  );

  const companyTextField = (
    <Autocomplete.TextField
      onChange={updateCompanyText}
      label="Compagnia"
      value={companyInputValue}
      prefix={<Icon source={SearchMinor as any} color="base" />}
      placeholder="Cerca compagnia"
      autoComplete="off"
      error={emptyFields.company && 'Per favore seleziona una compagnia'}
    />
  );

  const emptyCompanyState = (
    <React.Fragment>
      <div style={{ textAlign: 'center' }}>
        <TextContainer>Nessun cliente trovato</TextContainer>
      </div>
    </React.Fragment>
  );

  /**
   * Company markup
   *
   * Display autocomplete if company is not selected
   * When company is loaded, display company details
   */
  const companyMarkup =
    company === null ? (
      <LegacyStack vertical>
        <Autocomplete
          options={companyOptions}
          selected={selectedCompanyOptions}
          onSelect={updateCompanySelection}
          loading={companyLoading}
          textField={companyTextField}
          emptyState={emptyCompanyState}
        />
      </LegacyStack>
    ) : (
      <HorizontalStack blockAlign="center" gap="5">
        {company.logo && <Thumbnail size="medium" alt={company.logo.title} source={process.env.REACT_APP_BLOB_IMAGES_URL + company.logo.key} />}
        <Text as="span" fontWeight="semibold">
          {company.name}
        </Text>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            icon={CancelSmallMinor as any}
            onClick={() => {
              setCompany(null);
              setSelectedCompanyOptions([]);
              setCompanyInputValue('');
            }}
            plain
          />
        </div>
      </HorizontalStack>
    );

  /**
   * Contextual Save Bar
   */
  const handleDiscard = useCallback(() => {
    setIsDirty(false);

    // Reset all the fields
    setName('');
    setDescription('');
    setPrice('');
    setSalePrice('');
    setWarranties(['1']);
    setHasWarranties(false);
    setVariantWarranties([]);
  }, []);

  const contextualSaveBarMarkup = isDirty ? (
    <ContextualSaveBar
      message={'Modifiche non salvate'}
      saveAction={{
        onAction: handleSave,
        loading: buttonSpinning,
      }}
      discardAction={{
        onAction: handleDiscard,
        discardConfirmationModal: true,
      }}
      contextControl={contextControlMarkup}
    />
  ) : null;

  /**
   * Variant list markup
   */
  const variantListMarkup = (
    <ul className="variantList">
      {variants.map((variant) => (
        <li key={variant._id} className="variantItem">
          <UnstyledLink
            url={variant._id !== params.variantId ? `/admin/products/${params.id}/variants/${variant._id}` : null}
            className={`variantItemStack ${variant._id === params.variantId && 'active'}`}
          >
            <LegacyStack>
              <LegacyStack.Item>
                <Icon source={FraudProtectMajor as any} />
              </LegacyStack.Item>
              <LegacyStack.Item>
                <span className="variantItemLink">{variant.name}</span>
              </LegacyStack.Item>
            </LegacyStack>
          </UnstyledLink>
        </li>
      ))}
    </ul>
  );

  /** Notifaction email removal */
  const removeTag = useCallback(
    (tag: string) => () => {
      setSelectedTags((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
      setIsDirty(true);
    },
    [],
  );

  /** Notification Tag Markup */
  const notificationTagMarkup = selectedTags.map((option) => (
    <Tag key={option} onRemove={removeTag(option)}>
      {option}
    </Tag>
  ));

  /** Add shared notification mail when button clicked based on newTag state */
  const addTag = () => {
    const tagError = !validateEmail(newTag);
    if (tagError) {
      setTagError(true);
      return;
    } else {
      setTagError(false);
      newTag !== '' && !selectedTags.includes(newTag) && setSelectedTags([...selectedTags, newTag]);
      setNewTag('');
    }
  };

  const handleNewTagOnChange = useCallback((value: any) => {
    setNewTag(value);
    setIsDirty(true);
  }, []);

  /**
   * Error markups & toast
   */
  const toastMarkup = active ? <Toast content="La variante è stata creata con successo." onDismiss={toggleActive} /> : null;

  const saveErrorMarkup = saveError && (
    <Layout.Section fullWidth>
      <Banner title="Si è verificato un errore nel salvataggio dei dati" status="critical" onDismiss={() => setSaveError(false)}>
        <p>Si è pregati di riprovare più tardi.</p>
      </Banner>
    </Layout.Section>
  );

  /**
   * Page markup
   */
  const actualPageMarkup = (
    <Page title="Aggiungi variante" backAction={{ content: 'Varianti', url: `/admin/products/${params.id}` }}>
      <Layout>
        {/* Banner */}
        {saveErrorMarkup}

        <Layout.Section secondary>
          {/* Status */}
          <LegacyCard title="Varianti">
            <div className="variantListContainer">
              <Scrollable shadow>{variantListMarkup}</Scrollable>
            </div>
          </LegacyCard>
        </Layout.Section>

        <Layout.Section>
          {/* Name, description & group policy */}
          <LegacyCard>
            <LegacyCard.Section>
              <FormLayout>
                <TextField label="Nome" value={name} onChange={handleNameChange} autoComplete="off" error={emptyFields.name && 'Il nome è obbligatorio'} />
                <TextField label="Descrizione" value={description} onChange={handleDescriptionChange} autoComplete="off" />
                <Checkbox label="Preventivo da quotare manualmente" checked={toBeQuoted} onChange={handleToBeQuotedChange} />
                <Checkbox label="Prevede l'emissione diretta" checked={directEmission} onChange={handleDirectEmissionChange} />
              </FormLayout>
            </LegacyCard.Section>
            {/* Bank Transfer */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Da pagare tramite bonifico bancario" checked={bankTransfer} onChange={handleBankTransferChange} />
              </FormLayout>
            </LegacyCard.Section>
            {/* Group policy */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="È una polizza collettiva" checked={groupPolicy} onChange={handleGroupPolicyChange} />
                {groupPolicy && (
                  <>
                    <FormLayout.Group condensed>
                      <TextField label="Numero polizza" value={groupPolicyNumber} onChange={handleGroupPolicyNumberChange} autoComplete="off" />
                      <TextField
                        label="Prefisso numero inclusione"
                        value={groupPolicyInclusionNumber}
                        onChange={handleGroupPolicyInclusionNumberChange}
                        autoComplete="off"
                      />
                    </FormLayout.Group>
                    <FormLayout.Group condensed>
                      <TextField label="Broker" value={groupPolicyBroker} onChange={handleGroupPolicyBrokerChange} autoComplete="off" />
                      <TextField label="Ramo" value={groupPolicyBranch} onChange={handleGroupPolicyBranchChange} autoComplete="off" />
                    </FormLayout.Group>
                  </>
                )}
              </FormLayout>
            </LegacyCard.Section>
            {/* Disable sending notification */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Disattiva l'invio delle notifiche al cliente" checked={disableNotifications} onChange={handleDisableNotificationsChange} />
              </FormLayout>
            </LegacyCard.Section>
            <LegacyCard.Section>
              {/* Company */}
              {companyMarkup}
            </LegacyCard.Section>
          </LegacyCard>
          {/* Renewal */}
          <LegacyCard title="Rinnovo">
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Il prodotto prevedere il rinnovo automatico" checked={renewal.automatic} onChange={handleRenewalAutomaticChange} />
                {renewal.automatic && (
                  <FormLayout.Group>
                    <Checkbox
                      label="Il rinnovo prevedere di essere confermato?"
                      checked={renewal.to_be_confirmed}
                      onChange={handleRenewalToBeConfirmedChange}
                    />
                  </FormLayout.Group>
                )}
              </FormLayout>
            </LegacyCard.Section>
          </LegacyCard>
          {/* Variant type */}
          <LegacyCard sectioned title="Tipologia di variante">
            <FormLayout>
              <FormLayout.Group>
                <Select label="Tipologia di variante" options={variantTypeOptions} onChange={handleVariantTypeChange} value={selectedVariantType} />
                {selectedVariantType === 'api' && (
                  <TextField
                    label="Idenficatore api"
                    value={apiIdentifier}
                    onChange={handleApiIdentifierChange}
                    autoComplete="off"
                    error={emptyFields.api_identifier && "Il nome dell'identificatore api è obbligatorio"}
                  />
                )}
              </FormLayout.Group>
            </FormLayout>
          </LegacyCard>
          {/* Shared Notifications */}
          <LegacyCard title="Notifiche condivise">
            <LegacyCard.Section>
              <Checkbox
                label="Per questo prodotto sono previste notifiche condivise"
                checked={hasSharedNotification}
                onChange={handleHasSharedNotificationChange}
              />
            </LegacyCard.Section>
            {hasSharedNotification ? (
              <LegacyCard.Section>
                <FormLayout>
                  <LegacyStack spacing="tight">{notificationTagMarkup}</LegacyStack>
                  <TextField
                    label="Nuova mail"
                    value={newTag}
                    onChange={handleNewTagOnChange}
                    autoComplete="off"
                    error={tagError && 'Il formato della mail non è valido'}
                  />
                  <Button onClick={() => addTag()}>Aggiungi Mail</Button>
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>
          {/* Automation Bot */}
          <LegacyCard title="Automazione Ordine">
            <LegacyCard.Section>
              <Checkbox
                label="Per questo prodotto sono previste automazioni della registrazione dell'ordine"
                checked={hasAutomationBot}
                onChange={handleHasAutomationBotChange}
              />
            </LegacyCard.Section>
            {hasAutomationBot ? (
              <LegacyCard.Section>
                <FormLayout>
                  <LegacyStack spacing="tight">{notificationTagMarkup}</LegacyStack>
                  <Select label="Nome automazione" value={automationName} options={automationOptions} onChange={handleAutomationSelectChange} />
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Pricing */}
          <LegacyCard title="Prezzi">
            <LegacyCard.Section>
              <Checkbox
                label="Questo prodotto prevede l'utilizzo dei campi di quotazione per il calcolo del prezzo"
                checked={quotation}
                onChange={handleQuotationChange}
              />
            </LegacyCard.Section>
            {!quotation && (
              <LegacyCard.Section>
                <FormLayout>
                  <TextField
                    label="Prezzo"
                    type="currency"
                    placeholder="0,00"
                    value={price}
                    onChange={handlePriceChange}
                    autoComplete="off"
                    suffix="€"
                    error={emptyFields.price && 'Il prezzo è obbligatorio'}
                  />
                  <TextField
                    label="Prezzo promozionale"
                    type="currency"
                    placeholder="0,00"
                    value={salePrice}
                    onChange={handleSalePriceChange}
                    autoComplete="off"
                    suffix={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p style={{ marginRight: '5px' }}>€</p>
                        <Tooltip content="Inserisci qui il prezzo promozionale che vuoi mostrare al cliente">
                          <Icon source={QuestionMarkMajor as any} />
                        </Tooltip>
                      </div>
                    }
                  />
                </FormLayout>
              </LegacyCard.Section>
            )}
          </LegacyCard>

          {/** Campi di quotazione */}
          <LegacyCard title="Campi di quotazione">
            <LegacyCard.Section>{quotationMarkup}</LegacyCard.Section>
          </LegacyCard>

          {/* Blocking conditions */}
          <LegacyCard title="Condizioni bloccanti">
            <LegacyCard.Section>
              <Checkbox
                label="Questo prodotto prevede delle condizioni bloccanti"
                checked={hasBlockingConditions}
                onChange={handleHasBlockingConditionsChange}
              />
            </LegacyCard.Section>
            {hasBlockingConditions && blockingConditions.length > 0 && (
              <LegacyCard.Section>
                {blockingConditions.map((condition, index) => (
                  <LegacyCard.Section key={index}>
                    <LegacyStack alignment="center">
                      <LegacyStack.Item fill>
                        <TextField
                          key={index}
                          label={`Campo quotazione`}
                          value={condition.field}
                          onChange={(value) => handleBlockingConditionFieldChange(index, value)}
                          autoComplete="off"
                        />
                        <br></br>
                        <FormLayout>
                          {condition.conditions.map((subCondition, subIndex) => (
                            <LegacyCard sectioned key={subIndex}>
                              <TextField
                                label={`Regola ${subIndex + 1}`}
                                type="text"
                                value={subCondition.value}
                                autoComplete="off"
                                onChange={(value) => handleBlockingConditionValueChange(index, subIndex, value)}
                                connectedLeft={
                                  <Select
                                    label={`Regola ${subIndex + 1}`}
                                    labelHidden
                                    value={subCondition.rule}
                                    options={blockingConditionsRuleOptions}
                                    onChange={(value: RuleTypes) => handleBlockingConditionRuleChange(index, subIndex, value)}
                                  />
                                }
                                connectedRight={<Button icon={DeleteMinor as any} plain onClick={() => handleDeleteBlockingConditionRule(index, subIndex)} />}
                              />
                            </LegacyCard>
                          ))}
                        </FormLayout>
                        <LegacyCard.Section>
                          <Button plain removeUnderline icon={PlusMinor as any} onClick={() => handleAddBlockingConditionRule(index)}>
                            Aggiungi nuova regola
                          </Button>
                        </LegacyCard.Section>
                      </LegacyStack.Item>
                      <LegacyStack.Item>
                        <Button icon={DeleteMinor as any} plain onClick={() => handleDeleteBlockingCondition(index)} />
                      </LegacyStack.Item>
                    </LegacyStack>
                  </LegacyCard.Section>
                ))}
                <LegacyCard.Section>
                  <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddBlockingCondition}>
                    Aggiungi nuova condizione
                  </Button>
                </LegacyCard.Section>
              </LegacyCard.Section>
            )}
          </LegacyCard>

          {/* Pricing Rules */}
          {quotation && (
            <LegacyCard title="Regole di prezzo">
              <LegacyCard.Section>
                <Checkbox
                  label="Questo prodotto prevede delle regole da applicare alla quotazione"
                  checked={hasPricingRules}
                  onChange={handleHasPricingRulesChange}
                />
              </LegacyCard.Section>
              {hasPricingRules ? pricingRulesMarkup : null}
            </LegacyCard>
          )}
          {/* Commission */}
          <LegacyCard title="Provvigioni">
            <LegacyCard.Section>
              <Checkbox label="Questo prodotto prevede delle provvigioni per il broker" checked={hasCommission} onChange={handleHasCommissionChange} />
            </LegacyCard.Section>
            {hasCommission ? (
              <LegacyCard.Section>
                <FormLayout>
                  <FormLayout.Group condensed>
                    <Select
                      label="Tipo di provvigione"
                      options={commissionValueTypeOptions}
                      onChange={handleCommissionTypeChange}
                      value={commission.value_type}
                    />
                    <TextField
                      label="Valore"
                      type="currency"
                      placeholder="0,00"
                      value={commission.value}
                      onChange={handleCommissionValueChange}
                      autoComplete="off"
                      suffix={commission.value_type === 'fixed_amount' ? '€' : '%'}
                      min={0}
                      max={commission.value_type !== 'fixed_amount' ? 100 : undefined}
                      error={emptyFields.commission_value && 'Il prezzo è obbligatorio'}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Management price */}
          <LegacyCard title="Fattura di gestione">
            <LegacyCard.Section>
              <Checkbox
                label="Questo prodotto prevede l'aggiunta di una fattura di gestione"
                checked={hasManagementPrice}
                onChange={handleHasManagementPriceChange}
              />
            </LegacyCard.Section>
            {hasManagementPrice ? (
              <LegacyCard.Section>
                <FormLayout>
                  <FormLayout.Group condensed>
                    <Select
                      label="Tipo di pricing"
                      options={commissionValueTypeOptions}
                      onChange={handleManagementPriceTypeChange}
                      value={managementPrice.value_type}
                    />
                    <TextField
                      label="Valore"
                      type="currency"
                      placeholder="0,00"
                      value={managementPrice.value}
                      onChange={handleManagementPriceValueChange}
                      autoComplete="off"
                      suffix={managementPrice.value_type === 'fixed_amount' ? '€' : '%'}
                      min={0}
                      max={managementPrice.value_type !== 'fixed_amount' ? 100 : undefined}
                      error={emptyFields.management_price_value && 'Il prezzo è obbligatorio'}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Warranties */}
          <LegacyCard title="Garanzie">
            <LegacyCard.Section>
              <Checkbox label="Questo prodotto ha garanzie" checked={hasWarranties} onChange={handleHasWarrantiesChange} />
            </LegacyCard.Section>
            {hasWarranties && warrantyMarkup}
          </LegacyCard>
          {/* Files */}
          <LegacyCard
            title="Documenti"
            actions={[
              {
                content: 'Carica file',
                onAction: toggleOpenFileDialog,
              },
            ]}
          >
            <LegacyCard.Section>
              <FormLayout>{dropZoneMarkup}</FormLayout>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>

        <Layout.Section />
      </Layout>
    </Page>
  );

  // ---- Loading ----
  const loadingPageMarkup = (
    <SkeletonPage>
      <Layout>
        <Layout.Section>
          <LegacyCard sectioned>
            <TextContainer>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={9} />
            </TextContainer>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );

  // ---- Error ----
  const errorPageMarkup = (
    <Page>
      <Layout>
        <Layout.Section>
          <LegacyCard sectioned>
            <EmptyState
              heading="Nessua presente a questo indirizzo"
              image="https://cdn.shopify.com/shopifycloud/web/assets/v1/08f1b23a19257042c52beca099d900b0.svg"
            >
              <p>Controlla l'URL e riprova oppure usa la barra di ricerca per trovare ciò di cui hai bisogno.</p>
            </EmptyState>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </Page>
  );

  const pageMarkup = isLoading ? loadingPageMarkup : loadError ? errorPageMarkup : actualPageMarkup;

  return (
    <Frame
      topBar={<TopBarMarkup user={user} handleMobileNavigation={handleMobileNavigation} />}
      navigation={<NavigationMarkup user={user} />}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
      skipToContentTarget={skipToContentRef}
    >
      {contextualSaveBarMarkup}
      {pageMarkup}
      {toastMarkup}
    </Frame>
  );
}
